<template>
    <div class="ve-query-header">
        <div
            class="ve-query-header__title"
            :class="{ 'is-sortable': sortable }"
            :style="titleStyle"
            :role="sortable ? 'button' : null"
            :tabindex="sortable ? 0 : null"
            :aria-label="sortable ? title : null"
            :aria-sort="sortable ? ariaSort : null"
            @click="emitTitleClick"
            @keydown.enter.prevent="emitTitleClick"
            @keydown.space.prevent="emitTitleClick"
        >
            <span>{{ title }}</span>
        </div>
        <div v-if="isNumber" class="ve-query-header__control">
            <select class="ve-query-header__operator" :value="operator || defaultOperator" :title="title" @change="$emit('operator-change', $event.target.value)">
                <option value="=">=</option>
                <option value=">">&gt;</option>
                <option value="<">&lt;</option>
                <option value=">=">&gt;=</option>
                <option value="<=">&lt;=</option>
                <option value="!=">!=</option>
            </select>
            <input
                class="ve-query-header__input"
                type="number"
                :value="value"
                :title="title"
                @input="$emit('input', $event.target.value)"
                @keydown="preventNumberKey"
                @keydown.enter="$emit('query')"
            />
        </div>
        <el-date-picker
            v-else-if="isDate"
            :value="value"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder=""
            clearable
            class="ve-query-header__date"
            :title="title"
            @input="$emit('input', $event)"
            @change="$emit('query')"
        ></el-date-picker>
        <el-select
            v-else-if="isSelect"
            :value="value"
            :title="title"
            placeholder=""
            size="mini"
            clearable
            class="ve-query-header__select"
            @input="$emit('input', $event)"
            @change="$emit('query')"
        >
            <el-option v-for="option in normalizedOptions" :key="option.value" :value="option.value" :label="option.label"></el-option>
        </el-select>
        <input
            v-else-if="isTime"
            class="ve-query-header__input ve-query-header__input--full"
            type="text"
            :value="value"
            :title="title"
            @input="$emit('input', $event.target.value)"
            @change="$emit('query')"
            @keydown.enter="$emit('query')"
        />
        <input
            v-else
            class="ve-query-header__input ve-query-header__input--full"
            type="text"
            :value="value"
            :title="title"
            :disabled="isImage"
            @input="$emit('input', $event.target.value)"
            @keydown.enter="$emit('query')"
        />
    </div>
</template>

<script>
export default {
    name: 'VeTableQueryHeader',
    props: {
        title: { type: String, default: '' },
        edcss: { type: String, default: '' },
        value: { default: '' },
        operator: { type: String, default: '' },
        defaultOperator: { type: String, default: '=' },
        options: { type: Array, default: () => [] },
        titleStyle: { type: Object, default: () => ({}) },
        showDateOperator: { type: Boolean, default: false },
        sortable: { type: Boolean, default: false },
        sortOrder: { type: String, default: '' },
    },
    computed: {
        normalizedType(){
            return String(this.edcss || '').toLowerCase()
        },
        isNumber(){
            return this.normalizedType === 'number'
        },
        isDate(){
            return this.normalizedType === 'date' || this.normalizedType === 'date2'
        },
        isTime(){
            return this.normalizedType === 'time'
        },
        isImage(){
            return this.normalizedType === 'image'
        },
        isSelect(){
            return this.normalizedType === 'select' || this.normalizedType === 'checkbox' || this.options.length > 0
        },
        normalizedOptions(){
            if (this.normalizedType === 'checkbox' && this.options.length === 0) {
                return [
                    { value: 'Y', label: this.$LANG('2047', '是') },
                    { value: 'N', label: this.$LANG('2044', '否') },
                ]
            }
            return this.options.map(option => ({
                value: option.value !== undefined ? option.value : option.CODE,
                label: option.label !== undefined ? option.label : option.CNAME,
            }))
        },
        ariaSort(){
            if (this.sortOrder === 'asc') return 'ascending'
            if (this.sortOrder === 'desc') return 'descending'
            return 'none'
        },
    },
    methods: {
        emitTitleClick(){
            if (this.sortable) this.$emit('title-click')
        },
        preventNumberKey(event){
            if (event.key === 'e' || event.key === 'E' || event.key === '+') event.preventDefault()
        },
    },
}
</script>

<style scoped>
.ve-query-header {
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
}
.ve-query-header__title {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-height: 27px;
    margin-top: 3px;
    padding-left: 4px;
    line-height: 24px;
    text-align: left;
    color: #03274b;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    box-sizing: border-box;
}
.ve-query-header__title.is-sortable {
    cursor: pointer;
}
.ve-query-header__control {
    display: flex;
}
.ve-query-header__operator {
    width: 60px;
    height: 28px;
    margin-top: 1px;
    background: #fff;
    border: 1px solid #8b929d;
    box-sizing: border-box;
}
.ve-query-header__input {
    min-width: 0;
    width: 100%;
    height: 28px;
    margin-top: 1px;
    padding: 0;
    border: 1px solid #8b929d;
    background: #fff;
    box-sizing: border-box;
}
.ve-query-header__input--full,
.ve-query-header__select,
.ve-query-header__date {
    width: 100%;
    box-sizing: border-box;
}
.ve-query-header__select {
    width: 100%;
}
.ve-query-header__date {
    height: 28px;
    margin-top: 1px;
}
.ve-query-header__select /deep/ .el-input__inner,
.ve-query-header__date /deep/ .el-input__inner {
    height: 28px;
    padding: 0 8px;
    border: 1px solid #8b929d;
    background: #fff;
    box-sizing: border-box;
}
</style>
